import { useEffect, useCallback } from "react";
import { Events, trackEvent } from "../lib/analytics";

/**
 * Tracks clipboard copy events for analytics.
 * Detects native copies (keyboard, right-click, Edit menu) on a container element
 * and provides a callback for button-triggered copies.
 *
 * @param {object} options
 * @param {string} options.patientId - Patient ID for event properties and grouping
 * @param {string} options.org - Organization name
 * @param {React.RefObject} [options.containerRef] - Ref to the element to monitor for native copy events
 */
export function useCopyTracking({ patientId, org, containerRef }) {
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    const handleCopy = () => {
      trackEvent(
        Events.PATIENT_NOTE_COPIED,
        { patient_id: patientId, copy_method: "native", org },
        { groups: { patient: patientId } },
      );
    };

    el.addEventListener("copy", handleCopy);
    return () => el.removeEventListener("copy", handleCopy);
  }, [patientId, org, containerRef]);

  const trackButtonCopy = useCallback(
    (contentType = "note") => {
      trackEvent(
        Events.PATIENT_NOTE_COPIED,
        { patient_id: patientId, copy_method: "button", content_type: contentType, org },
        { groups: { patient: patientId } },
      );
    },
    [patientId, org],
  );

  return { trackButtonCopy };
}
